import React, { useEffect, useState, useCallback } from "react";
import AdminLayout from "./AdminLayout";
import api from "../../lib/api";
import { Search as SearchIcon, RefreshCw, TrendingUp, AlertCircle, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const RANGES = [
    { id: "24h", label: "24h" },
    { id: "7d", label: "7d" },
    { id: "30d", label: "30d" },
    { id: "all", label: "All" },
];

function formatTime(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
}

export default function AdminSearchIntel() {
    const [data, setData] = useState(null);
    const [range, setRange] = useState("7d");
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        setLoading(true);
        api.get(`/admin/search-intel?range=${range}&limit=25`)
            .then((r) => setData(r.data))
            .catch(() => toast.error("Failed to load search intelligence"))
            .finally(() => setLoading(false));
    }, [range]);

    useEffect(() => { load(); }, [load]);

    const total = data?.summary?.total ?? 0;
    const zero = data?.summary?.zero_result_total ?? 0;
    const zeroRate = total ? Math.round((zero / total) * 100) : 0;
    const aud = data?.summary?.audience ?? {};

    return (
        <AdminLayout>
            <div className="p-8 sm:p-10 max-w-6xl">
                <header className="mb-8">
                    <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] mb-3">
                        <SearchIcon className="w-3.5 h-3.5" />
                        Operations · Research demand
                    </div>
                    <div className="flex items-end justify-between flex-wrap gap-4">
                        <div>
                            <h1 className="text-3xl font-bold text-white tracking-tight">Search intelligence</h1>
                            <p className="text-sm text-[#94A3B8] mt-2 max-w-2xl leading-relaxed">
                                What visitors are searching for inside TCP — including the searches that found nothing. Zero-result queries are the strongest signal of what to investigate next.
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="inline-flex bg-[#111827] border border-[#243044] rounded-xl overflow-hidden" data-testid="search-intel-range">
                                {RANGES.map((r) => (
                                    <button
                                        key={r.id}
                                        onClick={() => setRange(r.id)}
                                        data-testid={`search-intel-range-${r.id}`}
                                        className={`px-3 py-2 text-xs uppercase tracking-[0.18em] transition-colors ${range === r.id ? "bg-[#2F80ED] text-white" : "text-[#94A3B8] hover:text-white"}`}
                                    >
                                        {r.label}
                                    </button>
                                ))}
                            </div>
                            <button
                                data-testid="search-intel-refresh"
                                onClick={load}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs uppercase tracking-[0.18em] text-[#94A3B8] hover:text-white border border-[#243044] hover:border-[#2F80ED]"
                            >
                                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                                Refresh
                            </button>
                        </div>
                    </div>
                </header>

                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
                    <Tile label="Total searches" value={total} accent="cyan" />
                    <Tile label="Zero-result rate" value={`${zeroRate}%`} accent={zeroRate > 30 ? "amber" : "cyan"} data-testid="zero-rate" />
                    <Tile label="By members" value={aud.member || 0} accent="cyan" />
                    <Tile label="By public" value={aud.public || 0} accent="info" />
                </div>

                <section className="mb-10">
                    <h2 className="text-xs font-semibold uppercase tracking-[0.22em] text-[#64748B] mb-3 flex items-center gap-2">
                        <TrendingUp className="w-3.5 h-3.5" />
                        Trending (last 24h vs prior 24h)
                    </h2>
                    <Table
                        rows={data?.trending || []}
                        empty="No trending searches yet."
                        testId="search-trending"
                        columns={[
                            { key: "term", label: "Term", render: (r) => <span className="font-mono text-white">{r.term}</span> },
                            { key: "count_24h", label: "Today", className: "text-right" },
                            { key: "count_prev_24h", label: "Prior", className: "text-right text-[#64748B]" },
                            { key: "delta", label: "Δ", className: "text-right", render: (r) => <span className="text-[#56CCF2]">+{r.delta}</span> },
                        ]}
                    />
                </section>

                <section className="mb-10">
                    <h2 className="text-xs font-semibold uppercase tracking-[0.22em] text-[#64748B] mb-3 flex items-center gap-2">
                        <AlertCircle className="w-3.5 h-3.5 text-[#F4B731]" />
                        Zero-result searches · what to investigate next
                    </h2>
                    <Table
                        rows={data?.zero_results || []}
                        empty="No zero-result searches in this window — your content is covering demand."
                        testId="search-zero"
                        columns={[
                            { key: "term", label: "Term", render: (r) => <span className="font-mono text-white">{r.term}</span> },
                            { key: "count", label: "Searches", className: "text-right" },
                            { key: "members", label: "By members", className: "text-right text-[#56CCF2]" },
                            { key: "last_seen", label: "Last seen", render: (r) => <span className="text-[#94A3B8]">{formatTime(r.last_seen)}</span> },
                        ]}
                    />
                </section>

                <section>
                    <h2 className="text-xs font-semibold uppercase tracking-[0.22em] text-[#64748B] mb-3">Top searches</h2>
                    <Table
                        rows={data?.top || []}
                        empty="No searches in this window."
                        testId="search-top"
                        columns={[
                            { key: "term", label: "Term", render: (r) => <span className="font-mono text-white">{r.term}</span> },
                            { key: "count", label: "Searches", className: "text-right" },
                            { key: "avg_results", label: "Avg results", className: "text-right text-[#94A3B8]" },
                            { key: "members", label: "Members", className: "text-right text-[#56CCF2]" },
                            { key: "last_seen", label: "Last seen", render: (r) => <span className="text-[#94A3B8]">{formatTime(r.last_seen)}</span> },
                        ]}
                    />
                </section>
            </div>
        </AdminLayout>
    );
}

function Tile({ label, value, accent = "cyan", "data-testid": testId }) {
    const dot = accent === "amber" ? "bg-[#F4B731]" : accent === "info" ? "bg-[#94A3B8]" : "bg-[#56CCF2]";
    return (
        <div className="bg-[#111827] border border-[#243044] rounded-xl p-5" data-testid={testId}>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#64748B] mb-3">
                <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
                {label}
            </div>
            <div className="text-3xl font-bold text-white tracking-tight">{value}</div>
        </div>
    );
}

function Table({ rows, columns, empty, testId }) {
    if (!rows || rows.length === 0) {
        return (
            <div className="bg-[#111827] border border-[#243044] rounded-xl px-6 py-10 text-center text-sm text-[#64748B] italic">
                {empty}
            </div>
        );
    }
    return (
        <div className="bg-[#111827] border border-[#243044] rounded-xl overflow-hidden" data-testid={testId}>
            <div className="grid px-6 py-3 border-b border-[#243044] text-[10px] uppercase tracking-[0.22em] text-[#64748B]" style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0,1fr))` }}>
                {columns.map((c) => (
                    <div key={c.key} className={c.className || ""}>{c.label}</div>
                ))}
            </div>
            <ul className="divide-y divide-[#243044]">
                {rows.map((r, i) => (
                    <li key={`${r.term || r._id || i}-${i}`} className="grid px-6 py-3 items-center text-sm" style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0,1fr))` }}>
                        {columns.map((c) => (
                            <div key={c.key} className={c.className || "text-white"}>
                                {c.render ? c.render(r) : r[c.key]}
                            </div>
                        ))}
                    </li>
                ))}
            </ul>
        </div>
    );
}
